import { FC, useEffect, useState } from 'react';
import { Button, Empty, Flex, Input, Switch, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { LeftOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { FULL_DATE_FORMAT } from 'constants/dates';
import { useNavigate } from 'react-router-dom';
import { AnalyzeStatuses } from 'constants/patients';
import { AnalysisRow, fetchAnalysisById } from 'services/api/analyses';

import { AnalysisDetailType } from './types';
import { renderReferenceRange, renderStatus } from './_utils';
import {
  Content,
  ResultBody,
  StyledDate,
  Table,
  TableData,
  TableHeader,
  TableRow,
  TestTitle
} from './styled';
import { Loading } from 'components/ui';

interface IProps {
  id: string;
}

const { Paragraph, Text } = Typography;

const AnalyzeResultTable: FC<IProps> = ({ id }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(false);
  const [result, setResult] = useState<AnalysisRow | null>(null);
  const [loading, setLoading] = useState(!!id);

  useEffect(() => {
    if (!id) {
      return;
    }

    setLoading(true);
    fetchAnalysisById(id)
      .then(setResult)
      .catch(() => setResult(null))
      .finally(() => setLoading(false));
  }, [id]);

  const onToggleSwitch = () => {
    setActive(!active);
  };

  const onSearch = (value: string) => {
    setSearch(value);
  };

  const filteredResults = Object.entries(result?.analysis || {}).filter(
    ([testName, testDetails]) => {
      const details = testDetails as AnalysisDetailType;

      const matchesSearch = testName
        .toLowerCase()
        .includes(search.toLowerCase());

      const matchesStatus = active
        ? details.status === AnalyzeStatuses.low ||
          details.status === AnalyzeStatuses.high
        : true;

      return matchesSearch && matchesStatus;
    }
  );

  if (loading) {
    return <Loading />;
  }

  return (
    <Content>
      <Flex justify="space-between" align="center">
        <Flex align="center">
          <Button
            style={{ color: '#18212c' }}
            type="link"
            icon={<LeftOutlined />}
            onClick={() => navigate(-1)}
          />
          <TestTitle level={4}>
            {result?.title?.replace(/(^"|"$)/g, '')}
          </TestTitle>
        </Flex>
        <StyledDate>
          {`${t('patient.date')}: `}
          {String(dayjs(result?.createdAt).format(FULL_DATE_FORMAT))}
        </StyledDate>
      </Flex>
      <Flex vertical flex={1}>
        <Flex justify="space-between" align="center" gap={24}>
          <Input
            style={{ maxWidth: 513 }}
            size="large"
            placeholder={t('patient.search_by_title')}
            onChange={e => onSearch(e.target.value)}
          />
          <Flex align="center">
            <Button
              style={{ color: '#18212c' }}
              type="link"
              onClick={onToggleSwitch}
            >
              <Text>{t('patient.show_abnormal_results')}</Text>
            </Button>
            <Switch value={active} onChange={onToggleSwitch} />
          </Flex>
        </Flex>
        {filteredResults.length ? (
          <ResultBody>
            <Table>
              <thead>
                <TableRow>
                  <TableHeader style={{ paddingLeft: 16 }}>
                    {t('patient.test_name')}
                  </TableHeader>
                  <TableHeader>{t('patient.result')}</TableHeader>
                  <TableHeader>{t('patient.normal_range')}</TableHeader>
                  <TableHeader>{t('patient.status')}</TableHeader>
                </TableRow>
              </thead>
              <tbody>
                {filteredResults.map(([testName, testDetails]) => {
                  const details = testDetails as AnalysisDetailType;

                  return (
                    <TableRow key={testName}>
                      <TableData style={{ paddingLeft: 16 }}>
                        {testName}
                      </TableData>
                      <TableData>
                        {details.result || (
                          <Paragraph style={{ paddingLeft: 20 }}>-</Paragraph>
                        )}
                      </TableData>
                      <TableData>
                        {renderReferenceRange(details.reference_range)}
                      </TableData>
                      <TableData>{renderStatus(details.status, t)}</TableData>
                    </TableRow>
                  );
                })}
              </tbody>
            </Table>
          </ResultBody>
        ) : (
          <Flex justify="center" align="center" flex={1}>
            <Empty />
          </Flex>
        )}
      </Flex>
    </Content>
  );
};

export default AnalyzeResultTable;
